import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ChevronDown, ChevronRight, Eye, EyeOff, Loader2, Plus, Scissors, Trash2 } from 'lucide-react';

import { LAYER_NORM_CUSTOM, LAYER_NORM_NONE } from '../../../constants/layers.ts';
import { useViewModeTransition } from '../../../context/ViewModeTransitionContext.tsx';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
  deletePlane,
  selectPlane,
  setPlaneDrawActive,
  setPointsView,
  startExclusionDraw,
  undoPlane,
  updatePlane,
} from '../../../store/reducers/measurementPlanes.reducer.ts';
import { toggleSection } from '../../../store/reducers/workspace.reducer.ts';
import { getNorms } from '../../../store/selectors/user.selector.ts';
import {
  type ExpectedActualPair,
  getActualValueColor,
  getComparisonOperator,
  isTargetSatisfied,
} from '../../../utils/normSatisfactionUtils';
import { getPhotometricSymbol } from '../../../utils/photometricSymbols';
import { getPhotometricUnit } from '../../../utils/unitSystem';
import { CustomTargetsForm } from '../../photometric-targets/CustomTargetsForm';
import { NormChipGrid } from '../scene/measurementPlane/NormChipGrid.tsx';

const FIELD = 'w-full bg-[#1a1a1a] border border-white/10 rounded-md text-xs text-white px-2 py-1';
const LABEL = 'text-[9px] uppercase tracking-wide text-gray-300 mb-0.5';

/** Per-surface measurement-point spacing options (metres). Smaller = denser points. */
const SPACING_OPTIONS = [0.25, 0.5, 1];

/** Small "drawn polygon" glyph for the section header (polygon outline + vertex dots). */
function PolygonIcon({ size = 18, className }: { size?: number; className?: string }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinejoin="round"
      className={className}
    >
      <polygon points="4,9 11,3 20,8 17,19 7,18" />
      <g fill="currentColor" stroke="none">
        <circle cx="4" cy="9" r="1.7" />
        <circle cx="11" cy="3" r="1.7" />
        <circle cx="20" cy="8" r="1.7" />
        <circle cx="17" cy="19" r="1.7" />
        <circle cx="7" cy="18" r="1.7" />
      </g>
    </svg>
  );
}

/** Collapsible sidebar section listing the drawn measurement surfaces. `embedded` drops the
 *  collapsible header and the add button (the workspace panel gives it its own section, with
 *  the button in its footer) and explains the feature while there are no surfaces yet. */
export function MeasurementSurfacesPanel({ embedded = false }: { embedded?: boolean }): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { transitionToMode } = useViewModeTransition();
  const sectionExpanded = useAppSelector((s) => s.workspace.expandedSections.measurementSurfaces);
  const expanded = embedded || sectionExpanded;
  const planes = useAppSelector((s) => s.measurementPlanes.planes);
  const results = useAppSelector((s) => s.measurementPlanes.results);
  const selectedId = useAppSelector((s) => s.measurementPlanes.selectedId);
  const computing = useAppSelector((s) => s.measurementPlanes.computing);
  const gridSpacing = useAppSelector((s) => s.workspace.measurementGridSpacing);
  const norms = useSelector(getNorms);
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const suggestedNorms = useMemo(
    () => [
      ...new Set(
        modelLayers
          .map((l) => l.selectedNorm)
          .filter((n): n is string => !!n && n !== LAYER_NORM_NONE && n !== LAYER_NORM_CUSTOM),
      ),
    ],
    [modelLayers],
  );
  // Surface whose norm chips are open (one at a time).
  const [normPickerFor, setNormPickerFor] = useState<string | null>(null);

  // Ctrl/Cmd+Z undoes surface edits while the section is open (ignored while typing).
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        dispatch(undoPlane());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded, dispatch]);

  return (
    <div className={embedded ? '' : `border-b border-black flex flex-col ${expanded ? 'flex-1 min-h-0' : ''}`}>
      {!embedded && (
        <button
          className={`w-full flex items-center justify-between p-3 transition-colors shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${
            expanded ? 'bg-stone-800 text-lime-400' : 'hover:bg-stone-900 hover:text-lime-500'
          }`}
          onClick={() => dispatch(toggleSection('measurementSurfaces'))}
        >
          <div className="flex items-center">
            <PolygonIcon size={18} className={`mr-2 ${expanded ? 'text-lime-400' : ''}`} />
            <span className={expanded ? 'text-lime-400' : ''}>{t('workspace.panel.surfaces.title')}</span>
            {planes.length > 0 && <span className="ml-2 text-xs text-gray-300">({planes.length})</span>}
          </div>
          <ChevronRight
            size={16}
            className={`ml-2 transform transition-transform ${expanded ? 'rotate-90 text-lime-400' : 'text-gray-500'}`}
          />
        </button>
      )}

      <div className={embedded ? 'block' : expanded ? 'block overflow-y-auto flex-1 min-h-0' : 'hidden'}>
        <div className={embedded ? 'p-3 space-y-2' : 'p-2 space-y-1.5'}>
          {!embedded && (
            <button
              onClick={() => {
                // Drawing happens in the measurement view (Surface points) — jump there.
                transitionToMode('measurement');
                dispatch(setPointsView('surface'));
                dispatch(setPlaneDrawActive(true));
              }}
              className="w-full flex items-center justify-center gap-1.5 text-xs text-lime-400 border border-lime-400/30 rounded-md py-1.5 hover:bg-lime-400/10"
            >
              <Plus size={14} /> {t('workspace.panel.surfaces.add')}
            </button>
          )}

          {embedded && planes.length === 0 && (
            <div className="px-1 pt-1 pb-2">
              <p className="text-[13px] font-medium text-white">{t('workspace.panel.surfaces.introTitle')}</p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-gray-400">
                {t('workspace.panel.surfaces.introText')}
              </p>
              <ul className="mt-3 space-y-1.5 text-[12px] leading-snug text-gray-300">
                <li>• {t('workspace.panel.surfaces.introStep1')}</li>
                <li>• {t('workspace.panel.surfaces.introStep2')}</li>
                <li>• {t('workspace.panel.surfaces.introStep3')}</li>
              </ul>
            </div>
          )}

          {!embedded && planes.length === 0 && (
            <div className="px-1 py-2 text-xs text-gray-300">{t('workspace.panel.surfaces.empty')}</div>
          )}

          {planes.map((plane) => {
            const result = results.find((r) => r.uuid === plane.id);
            const pairs: ExpectedActualPair[] = [];
            if (result) {
              for (const expected of result.expectedValues) {
                const actual = result.actualValues.find(
                  (a) => a.function === expected.function && a.type === expected.type,
                );
                if (actual) {
                  pairs.push({
                    expected: expected.value,
                    actual: actual.value,
                    function: expected.function,
                    type: expected.type,
                  });
                }
              }
            }
            const satisfied = pairs.filter(isTargetSatisfied).length;
            const eValues = (result?.actualValues ?? []).filter((v) => v.function === 'E');
            const isSelected = plane.id === selectedId;

            return (
              <div
                key={plane.id}
                onClick={() => dispatch(selectPlane(plane.id))}
                className={`rounded-lg overflow-hidden border cursor-pointer transition-all duration-200 ${
                  isSelected ? 'border-lime-400 ring-1 ring-lime-400' : 'border-neutral-700'
                }`}
                style={{
                  background: 'linear-gradient(170deg, rgba(79, 209, 46, 0.12) 0%, rgba(35, 35, 35, 0.95) 100%)',
                }}
              >
                {/* Title row */}
                <div className="flex items-center gap-1 px-2.5 py-1.5 min-w-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(updatePlane({ id: plane.id, changes: { visible: !plane.visible } }));
                    }}
                    className="text-gray-400 hover:text-white flex-shrink-0"
                    title={plane.visible ? t('workspace.panel.surfaces.hide') : t('workspace.panel.surfaces.show')}
                  >
                    {plane.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <input
                    value={plane.name}
                    title={t('workspace.panel.surfaces.rename')}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => dispatch(updatePlane({ id: plane.id, changes: { name: e.target.value } }))}
                    className="text-[13px] font-medium text-white bg-transparent border-b border-transparent hover:border-white/20 focus:border-lime-400 outline-none min-w-0 flex-1"
                  />
                  <span className="text-[9px] px-1 py-0.5 rounded bg-white/10 text-gray-300 flex-shrink-0 uppercase tracking-wide">
                    {plane.frame ? t('workspace.panel.surfaces.vertical') : t('workspace.panel.surfaces.horizontal')}
                  </span>
                  <div className="flex items-center gap-1 ml-auto flex-shrink-0 text-[11px]">
                    {pairs.length > 0 && (
                      <span className="whitespace-nowrap ml-1">
                        <span className="text-gray-300">{t('workspace.satisfied')}: </span>
                        <span className="text-lime-400 font-semibold">{satisfied}</span>
                        <span className="text-gray-300">/{pairs.length}</span>
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(deletePlane(plane.id));
                      }}
                      title={t('workspace.panel.surfaces.delete')}
                      className="ml-1 rounded p-0.5 text-red-400 hover:bg-red-400/15 hover:text-red-300"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                {/* Norm + point spacing side by side (titles above). Height isn't editable —
                    points are draped onto the actual surface. */}
                <div className="px-2.5 pb-2 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-end gap-1.5">
                    <div className="flex-1 min-w-0">
                      <div className={LABEL}>{t('workspace.norm')}</div>
                      <button
                        type="button"
                        onClick={() => setNormPickerFor(normPickerFor === plane.id ? null : plane.id)}
                        aria-expanded={normPickerFor === plane.id}
                        className={`${FIELD} flex items-center justify-between text-left`}
                      >
                        <span
                          className={plane.normName || plane.customTargets?.length ? 'text-lime-400 font-medium' : ''}
                        >
                          {plane.customTargets?.length
                            ? t('workspace.customTargets.chip')
                            : (plane.normName ?? t('workspace.panel.surfaces.noNorm'))}
                        </span>
                        <ChevronDown
                          size={14}
                          className={`text-gray-400 transition-transform ${normPickerFor === plane.id ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={LABEL}>{t('workspace.panel.surfaces.pointSpacing')}</div>
                      <select
                        className={FIELD}
                        value={plane.spacing ?? gridSpacing}
                        onChange={(e) =>
                          dispatch(updatePlane({ id: plane.id, changes: { spacing: Number(e.target.value) } }))
                        }
                      >
                        {SPACING_OPTIONS.map((v) => (
                          <option key={v} value={v}>
                            {v} m
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {normPickerFor === plane.id && (
                    <>
                      {plane.customTargets?.length ? (
                        <button
                          type="button"
                          onClick={() =>
                            dispatch(
                              updatePlane({
                                id: plane.id,
                                changes: { normId: null, normName: null, customTargets: null },
                              }),
                            )
                          }
                          className="text-[12px] text-gray-400 hover:text-white"
                        >
                          {t('workspace.customTargets.useClass')}
                        </button>
                      ) : (
                        <NormChipGrid
                          norms={norms}
                          value={plane.normId}
                          suggested={suggestedNorms}
                          onPick={(id, name) => {
                            dispatch(
                              updatePlane({
                                id: plane.id,
                                changes: { normId: id, normName: name, customTargets: null },
                              }),
                            );
                            setNormPickerFor(null);
                          }}
                        />
                      )}
                      <div className="mt-2">
                        <CustomTargetsForm
                          quantities={plane.frame ? ['E'] : undefined}
                          value={plane.customTargets ?? []}
                          onApply={(targets) =>
                            dispatch(
                              updatePlane({
                                id: plane.id,
                                changes: targets.length
                                  ? { normId: null, normName: LAYER_NORM_CUSTOM, customTargets: targets }
                                  : { normId: null, normName: null, customTargets: null },
                              }),
                            )
                          }
                        />
                      </div>
                    </>
                  )}

                  {/* Cut away part of the surface you don't want measured (polygon). It's
                      excluded from the calculation; undo with Ctrl+Z. */}
                  {isSelected && (
                    <button
                      onClick={() => dispatch(startExclusionDraw({ planeId: plane.id, shape: 'polygon' }))}
                      title={t('workspace.panel.surfaces.cutOutHint')}
                      className="w-full flex items-center justify-center gap-1.5 rounded-md border border-red-400/30 py-1.5 text-[11px] text-red-300 hover:bg-red-400/10"
                    >
                      <Scissors size={13} /> {t('workspace.panel.surfaces.cutOut')}
                    </button>
                  )}
                </div>

                {/* While the worker runs, show progress instead of (stale) results. */}
                {computing && (
                  <div className="px-2.5 pb-2 flex items-center gap-1.5 text-[11px] text-lime-400/80">
                    <Loader2 size={12} className="animate-spin" /> {t('workspace.panel.surfaces.calculating')}
                  </div>
                )}

                {/* Per-norm results table */}
                {!computing && pairs.length > 0 && (
                  <div className="px-2.5 pb-2">
                    <div className="grid grid-cols-12 items-center text-[9px] text-gray-300 uppercase tracking-wide mb-0.5">
                      <div className="col-span-1" />
                      <div className="col-span-3" />
                      <div className="col-span-4">{t('workspace.value', 'Value')}</div>
                      <div className="col-span-4">{t('workspace.targetValue', 'Required')}</div>
                    </div>
                    {pairs.map((pair) => {
                      const uom = getPhotometricUnit(pair.function);
                      const ok = isTargetSatisfied(pair);
                      const operator = getComparisonOperator(pair.type);
                      return (
                        <div
                          key={`${pair.function}-${pair.type}`}
                          className="grid grid-cols-12 items-center text-[11px] h-[20px] border-b border-white/5"
                        >
                          <div className="col-span-1 flex justify-center">
                            <div className={`w-2 h-2 rounded-full ${ok ? 'bg-lime-400' : 'bg-red-400'}`} />
                          </div>
                          <div className="col-span-3 text-gray-300">
                            {getPhotometricSymbol(pair.function, pair.type)}
                          </div>
                          <div className={`col-span-4 ${getActualValueColor(pair)} font-medium`}>
                            {pair.actual?.toFixed(2)}
                            {uom ? ` ${uom}` : ''}
                          </div>
                          <div className="col-span-4 text-gray-300">
                            {operator} {pair.expected?.toFixed(2)}
                            {uom ? ` ${uom}` : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* No norm → show measured horizontal illuminance */}
                {!computing && pairs.length === 0 && (
                  <div className="px-2.5 pb-2 text-[11px]">
                    {eValues.length > 0 ? (
                      <div className="flex flex-wrap gap-x-3 text-white">
                        <span className="text-gray-300">E (lx):</span>
                        {eValues.map((v) => (
                          <span key={v.type}>
                            <span className="text-gray-300">{v.type}</span> {v.value.toFixed(2)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-300">{t('workspace.panel.surfaces.noResults')}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
